import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PublicLayout } from "@/components/PublicLayout";
import { PricingCard } from "@/components/PricingCard";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tv, Smartphone, Monitor, Tablet, Clapperboard, Zap, ShieldCheck, Headphones,
  Globe, Star, PlayCircle, ArrowRight, Film, Radio,
} from "lucide-react";

const features = [
  { icon: Radio, title: "22,000+ Live Channels", desc: "Global sports, news, movies and entertainment from around the world." },
  { icon: Film, title: "Massive VOD Library", desc: "Thousands of movies and series updated every week in HD & 4K." },
  { icon: Zap, title: "Anti-Freeze Tech", desc: "Buffer-free streaming on premium servers with 99.9% uptime." },
  { icon: Globe, title: "Worldwide Access", desc: "Watch anywhere, anytime on any internet connection." },
  { icon: ShieldCheck, title: "Secure & Private", desc: "Your data stays protected with encrypted, reliable delivery." },
  { icon: Headphones, title: "24/7 Support", desc: "Real humans ready to help you on WhatsApp whenever you need." },
];

const devices = [
  { icon: Tv, label: "Smart TV" },
  { icon: Smartphone, label: "Android & iOS" },
  { icon: Monitor, label: "Windows & Mac" },
  { icon: Tablet, label: "Tablets" },
  { icon: Clapperboard, label: "Firestick" },
  { icon: PlayCircle, label: "Android Box" },
];

export default function Home() {
  const navigate = useNavigate();
  const { data: packages = [] } = useQuery({ queryKey: ["packages"], queryFn: async () => (await api.get("/packages")).data });
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/testimonials")).data });
  const { data: faqs = [] } = useQuery({ queryKey: ["faqs"], queryFn: async () => (await api.get("/faqs")).data });

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative min-h-[90vh] flex flex-col justify-center items-center text-center px-4 overflow-hidden pt-28 pb-16 grid-pattern">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs font-semibold text-purple-300 border border-purple-500/30 mb-7 animate-float-up">
          <Star className="w-3.5 h-3.5 fill-purple-400 text-purple-400" /> Premium 4K IPTV Experience
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] max-w-4xl animate-float-up">
          Stream Everything in <span className="text-gradient">Stunning 4K</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl animate-float-up">
          Saad IPTV brings you 22,000+ live channels, movies and series across every device. Premium quality, zero buffering, one simple subscription.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-4 animate-float-up">
          <Button data-testid="hero-view-packages" onClick={() => navigate("/packages")} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0 px-8 py-6 text-base">
            View Packages <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button data-testid="hero-get-started" variant="outline" onClick={() => navigate("/register")} className="border-white/15 bg-white/5 text-white hover:bg-white/10 px-8 py-6 text-base">
            Get Started Free
          </Button>
        </div>
        <div className="mt-14 grid grid-cols-3 gap-8 max-w-lg w-full animate-float-up">
          {[["22K+", "Channels"], ["50K+", "Movies & Series"], ["99.9%", "Uptime"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-heading text-2xl sm:text-3xl font-extrabold text-white">{n}</div>
              <div className="text-xs text-slate-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-purple-400 mb-3">Why Saad IPTV</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Everything you need to stream</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (
            <div key={f.title} className="glass rounded-2xl p-7 hover:border-purple-500/40 transition-colors duration-300">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600/30 to-blue-600/30 border border-white/10 flex items-center justify-center mb-5">
                <f.icon className="w-6 h-6 text-neon-purple" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-purple-400 mb-3">Pricing</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Choose your plan</h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto">Flexible subscriptions for every viewer. Cancel anytime.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {packages.map((p) => <PricingCard key={p.id} pkg={p} />)}
        </div>
      </section>

      {/* DEVICES */}
      <section id="devices" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-purple-400 mb-3">Supported Devices</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-5">Watch on all your screens</h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Saad IPTV works seamlessly across every major platform. Set up in minutes and switch between devices without missing a beat.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {devices.map((d) => (
                <div key={d.label} className="glass rounded-xl p-4 flex flex-col items-center gap-2 hover:border-purple-500/40 transition-colors">
                  <d.icon className="w-7 h-7 text-neon-blue" />
                  <span className="text-xs text-slate-300 font-medium text-center">{d.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden glass p-2">
            <img
              src="https://images.unsplash.com/photo-1512070679279-8988d32161be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Streaming on devices"
              className="rounded-xl w-full h-[340px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-purple-400 mb-3">Testimonials</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Loved by thousands of viewers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((t) => (
            <div key={t.id} data-testid={`testimonial-${t.id}`} className="glass rounded-2xl p-6">
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-sm text-slate-300 leading-relaxed mb-5">“{t.message}”</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                  {t.name?.[0]}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-purple-400 mb-3">FAQ</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Frequently asked questions</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f) => (
            <AccordionItem key={f.id} value={f.id} data-testid={`faq-${f.id}`} className="glass rounded-xl px-5 border-white/10">
              <AccordionTrigger className="text-left text-white hover:no-underline text-sm sm:text-base font-medium">{f.question}</AccordionTrigger>
              <AccordionContent className="text-slate-400 text-sm leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative rounded-3xl overflow-hidden glass p-10 sm:p-16 text-center">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-transparent to-blue-600/20" />
          <div className="relative">
            <h2 className="font-heading text-2xl sm:text-4xl font-bold text-white mb-4">Ready to start streaming?</h2>
            <p className="text-slate-400 max-w-xl mx-auto mb-8">Join thousands of happy viewers. Pick a plan and get activated in minutes.</p>
            <Button data-testid="cta-get-started" onClick={() => navigate("/packages")} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0 px-8 py-6 text-base">
              Choose Your Plan <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
